import { Container, Graphics, Sprite, type Texture } from 'pixi.js';

/**
 * Illuminazione dinamica a due strati:
 *  - un overlay "ambiente" in moltiplicazione che tinge la scena secondo
 *    l'ora del giorno (alba → mezzogiorno → tramonto → notte)
 *  - sprite di luce additivi (anomalie, lanterna del giocatore) che bucano
 *    il buio quando l'ambiente si scurisce.
 */

interface AmbientStop {
  t: number; // ora normalizzata [0,1)
  color: [number, number, number];
}

// 0 = alba, 0.25 = mezzogiorno, 0.5 = tramonto, 0.75 = notte fonda
const AMBIENT_STOPS: AmbientStop[] = [
  { t: 0.0, color: [1.0, 0.88, 0.8] }, // alba rosata
  { t: 0.25, color: [1.0, 1.0, 1.0] }, // pieno giorno
  { t: 0.5, color: [1.0, 0.78, 0.62] }, // tramonto ambrato
  { t: 0.62, color: [0.45, 0.48, 0.72] }, // crepuscolo
  { t: 0.75, color: [0.3, 0.34, 0.58] }, // notte
  { t: 0.88, color: [0.5, 0.52, 0.72] }, // prima dell'alba
  { t: 1.0, color: [1.0, 0.88, 0.8] },
];

function ambientColor(t: number): [number, number, number] {
  const time = ((t % 1) + 1) % 1;
  for (let i = 0; i < AMBIENT_STOPS.length - 1; i++) {
    const a = AMBIENT_STOPS[i]!;
    const b = AMBIENT_STOPS[i + 1]!;
    if (time >= a.t && time <= b.t) {
      const k = (time - a.t) / (b.t - a.t);
      return [
        a.color[0] + (b.color[0] - a.color[0]) * k,
        a.color[1] + (b.color[1] - a.color[1]) * k,
        a.color[2] + (b.color[2] - a.color[2]) * k,
      ];
    }
  }
  return [1, 1, 1];
}

function toHex([r, g, b]: [number, number, number]): number {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return (c(r) << 16) | (c(g) << 8) | c(b);
}

export interface LightSource {
  sprite: Sprite;
  baseAlpha: number;
}

export class LightingSystem {
  /** Overlay a schermo intero, blend multiply. */
  readonly ambientOverlay: Graphics;
  /** Container in coordinate mondo per le luci additive. */
  readonly lightLayer: Container;
  private lightTexture: Texture;
  private lights: LightSource[] = [];

  constructor(screenW: number, screenH: number, glowTexture: Texture) {
    this.ambientOverlay = new Graphics();
    this.ambientOverlay.blendMode = 'multiply';
    this.resize(screenW, screenH);

    this.lightLayer = new Container();
    this.lightLayer.blendMode = 'add';

    this.lightTexture = glowTexture;
  }

  resize(w: number, h: number): void {
    this.ambientOverlay.clear();
    this.ambientOverlay.rect(0, 0, w, h).fill(0xffffff);
  }

  addLight(x: number, y: number, radius: number, tint: number, alpha = 0.8): LightSource {
    const sprite = new Sprite(this.lightTexture);
    sprite.anchor.set(0.5);
    sprite.position.set(x, y);
    sprite.width = radius * 2;
    sprite.height = radius * 2;
    sprite.tint = tint;
    sprite.alpha = 0;
    this.lightLayer.addChild(sprite);
    const source = { sprite, baseAlpha: alpha };
    this.lights.push(source);
    return source;
  }

  /**
   * Aggiorna tinta ambiente e intensità luci.
   * `timeOfDay` in [0,1); `elapsed` per la pulsazione delle luci.
   */
  update(timeOfDay: number, elapsed: number): void {
    const ambient = ambientColor(timeOfDay);
    this.ambientOverlay.tint = toHex(ambient);

    // le luci emergono quando l'ambiente si scurisce
    const darkness = 1 - (ambient[0] + ambient[1] + ambient[2]) / 3;
    const lightStrength = Math.min(1, Math.max(0, darkness * 2.2));
    for (const light of this.lights) {
      const pulse = 0.85 + 0.15 * Math.sin(elapsed * 2.1 + light.sprite.x * 0.05);
      light.sprite.alpha = light.baseAlpha * lightStrength * pulse;
    }
  }
}

export { ambientColor };
